import React, { FC, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Icon } from "../Icon/Icon";

interface IInputProps {
  name: string;
  id?: string;
  label?: string;
  placeholder?: string;
  type?: string;
  value?: string | number;
  error: string | undefined;
  isDisabled?: boolean;
  isRequired?: boolean;
  isSearch?: boolean;
  autoComplete?: "on" | "off";
  onBlur?: () => void;
  onChange?: (arg: string) => void;
  onFocus?: () => void;
  iconEnd?: any;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

const WrapperInput = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled.input<{ isError: boolean; isSearch?: boolean }>`
  width: 100%;
  padding: 0.375rem 0.75rem;
  border: none;
  color: #495057;
  background: var(--hero-background);
  color: var(--hero-text);
  height: 48px;
  border-radius: 4px;

  &:disabled {
    cursor: not-allowed;
  }
  &::placeholder {
    color: var(--hero-text);
  }

  &:focus {
    box-shadow: none;
    background: var(--hero-background);
    color: var(--hero-text);
    padding-bottom: 4px;
  }

  &:active {
    background: var(--hero-background);
    color: var(--hero-text);
    padding-bottom: 4px;
  }
`;

const StyledLabel = styled.span<{ isRequired?: boolean }>`
  display: inline-block;
  margin-bottom: 14px;
  font-size: 14px;
  line-height: 1.5em;
  color: var(--text);

  ${({ isRequired }) =>
    isRequired
      ? css`
          &::after {
            content: "*";
            color: var(--color-red-200);
          }
        `
      : css`
          &::after {
            content: "";
            display: none;
          }
        `};
`;

const ErrorMessage = styled.span`
  display: block;
  position: absolute;
  padding-top: 6px;
  font-size: 14px;
  line-height: 1.5em;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  color: var(--color-red-200);
`;

const StyledIcon = styled.div`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--hero-text);
`;

export const IconWrapper = styled.div<{ position: "start" | "end" }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0;
  position: absolute;
  margin: auto 0;
  bottom: 14px;
  right: 25px;
  z-index: 14;
`;

export const InputWithValidation: FC<IInputProps> = (props): JSX.Element => {
  const {
    name = "",
    label = "",
    id = "",
    placeholder = "",
    error = "",
    value = "",
    isDisabled = false,
    isRequired = false,
    isSearch = false,
    autoComplete = "off",
    onChange,
    onBlur,
    onFocus,
    iconEnd,
    onClick,
    onKeyDown,
  } = props;

  const [isFocus, setIsFocus] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => setInputValue(value), [value]);

  const handleBlur = () => {
    setIsFocus(false);
    if (onBlur) onBlur();
  };

  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    const inputValue: string = event.currentTarget.value;
    setInputValue(() => inputValue);

    if (!onChange) return;
    onChange(inputValue);
  };

  return (
    <WrapperInput>
      <StyledLabel isRequired={isRequired}>{label}</StyledLabel>
      {isSearch && (
        <StyledIcon>
          <Icon name="search" size={24} />
        </StyledIcon>
      )}
      <StyledInput
        id={id}
        name={name}
        isError={!!error}
        isSearch={isSearch}
        value={inputValue}
        placeholder={placeholder}
        onChange={(event) => handleChange(event)}
        onBlur={handleBlur}
        autoComplete={autoComplete}
        onFocus={() => {
          setIsFocus(!isFocus);
          onFocus && onFocus();
        }}
        onKeyDown={onKeyDown}
        onClick={onClick}
        disabled={isDisabled}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {iconEnd ? <IconWrapper position="end">{iconEnd}</IconWrapper> : null}
    </WrapperInput>
  );
};
